import { useEffect,useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import { LayoutQuestion } from '../LayoutQuestion';

export default function MatrixQuestion(props) {
  const [isMultipleChoice, setIsMultipleChoice] = useState(false);
  const [optionType, setOptionType] = useState('fixed'); // 'fixed' o 'dynamic'
  const [excelOptions, setExcelOptions] = useState([]);
  const [excelFileName, setExcelFileName] = useState('');
  const [hasOtherOption, setHasOtherOption] = useState(false); // Nueva state para controlar si hay opción "otra"
  const [otherOptionDescription, setOtherOptionDescription] = useState(''); // Descripción de la opción "otra"
  const [question, setQuestion] = useState({
    title: '',
    description: '',
    rows: [''],
    columns: [''],
    responses: {},
  });

  const addRow = () => {
    setQuestion((prev) => ({
      ...prev,
      rows: [...prev.rows, ''],
    }));

    props.setInformation((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        options: [...(prev.matrixOption.options || []), ''],
      },
    }));
  };

  const addColumn = () => {
    setQuestion((prev) => ({
      ...prev,
      columns: [...prev.columns, ''],
    }));

    props.setInformation((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        values: [...(prev.matrixOption.values || []), ''],
      },
    }));
  };

  // Nueva función para manejar el switch de la opción "otra"
  const handleOtherOptionToggle = (event) => {
    const newHasOtherOption = event.target.checked;
    setHasOtherOption(newHasOtherOption);
    
    if (!newHasOtherOption) {
      // Si se desactiva la opción "otra", limpiar la descripción y las respuestas relacionadas
      setOtherOptionDescription('');
      setQuestion((prev) => {
        const newResponses = { ...prev.responses };
        delete newResponses['otherOption'];
        return {
          ...prev,
          responses: newResponses,
        };
      });
    }

    // Actualizar la información en el componente padre
    props.setInformation((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        hasOtherOption: newHasOtherOption,
        otherOptionDescription: newHasOtherOption ? otherOptionDescription : '',
      },
    }));
  };

  const removeRow = (index) => {
    if (question.rows.length > 1) {
      setQuestion((prev) => ({
        ...prev,
        rows: prev.rows.filter((_, i) => i !== index),
      }));

      props.setInformation((prev) => ({
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          options: prev.matrixOption.options.filter((_, i) => i !== index),
        },
      }));
    }
  };

  const removeColumn = (index) => {
    if (question.columns.length > 1) {
      setQuestion((prev) => ({
        ...prev,
        columns: prev.columns.filter((_, i) => i !== index),
      }));
      props.setInformation((prev) => ({
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          values: prev.matrixOption.values.filter((_, i) => i !== index),
        },
      }));
    }
  };

  const updateRow = (index, value) => {
    setQuestion((prev) => ({
      ...prev,
      rows: prev.rows.map((row, i) => (i === index ? value : row)),
    }));

    props.setInformation((prev) => {
      const newValues = [...(prev.matrixOption.options || [])];
      newValues[index] = value;
      return {
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          options: newValues,
        },
      };
    });
  };

  const updateColumn = (index, value) => {
    setQuestion((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) => (i === index ? value : col)),
    }));

    props.setInformation((prev) => {
      const newValues = [...(prev.matrixOption.values || [])];
      newValues[index] = value;
      return {
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          values: newValues,
        },
      };
    });
  };

  // Nueva función para actualizar la descripción de la opción "otra"
  const updateOtherOptionDescription = (value) => {
    setOtherOptionDescription(value);
    props.setInformation((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        otherOptionDescription: value,
      },
    }));
  };

  const handleResponseChange = (rowIndex, columnIndex) => {
    setQuestion((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [rowIndex]: columnIndex.toString(),
      },
    }));
  };

  const handleDynamicResponseChange = (rowIndex, value) => {
    setQuestion((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [rowIndex]: value,
      },
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Extraer opciones de la primera columna (excluyendo encabezado si existe)
          const options = jsonData
            .slice(1) // Omitir primera fila (encabezado)
            .map((row) => row[0])
            .filter((option) => option && option.toString().trim() !== '');

          setExcelOptions(options);
          setExcelFileName(file.name);
          // Limpiar respuestas al cargar nuevo archivo
          setQuestion((prev) => ({ ...prev, responses: {} }));

          props.setInformation((prev) => ({
            ...prev,
            matrixOption: {
              ...prev.matrixOption,
              options: options,
            },
          }));
        } catch (error) {
          console.error('Error al leer el archivo Excel:', error);
          alert(
            'Error al procesar el archivo Excel. Asegúrate de que sea un archivo válido.'
          );
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const tableHeaderStyle = {
    border: '1px solid #ccc',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    minWidth: '120px',
    textAlign: 'center',
  };

  const tableCellStyle = {
    border: '1px solid #ccc',
    padding: '10px',
    textAlign: 'center',
  };

  const tableElementCellStyle = {
    ...tableCellStyle,
    textAlign: 'left',
    fontWeight: 'bold',
  };

  useEffect(() => {
    props.setInformation((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        matrixType: optionType,
      },
    }));
  }, [optionType]);

  useEffect(() => {
    props.setInformation((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        inputType: isMultipleChoice ? 'multiple' : 'single', // Guardar el texto
      },
    }));
  }, [isMultipleChoice]);

  return (
    <LayoutQuestion {...props}>
      <Box sx={{ maxWidth: 1000, margin: '0 auto', padding: 4 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 2 }}
        >
          {/* Nuevo select para tipo de opciones */}
          <FormControl size="small">
            <InputLabel id="option-type-label">Tipo de opciones</InputLabel>
            <Select
              labelId="option-type-label"
              value={optionType}
              onChange={(e) => {
                setOptionType(e.target.value);
                setQuestion((prev) => ({ ...prev, responses: {} })); // Limpiar respuestas al cambiar tipo
                if (e.target.value === 'fixed') {
                  setExcelOptions([]); // Limpiar opciones de Excel
                }
                // No resetear hasOtherOption - mantener la configuración para ambos tipos
              }}
              label="Tipo de opciones"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="fixed">Opciones fijas</MenuItem>
              <MenuItem value="dynamic">Opciones dinámicas</MenuItem>
            </Select>
          </FormControl>

          {/* Select para tipo de selección - siempre visible */}
          <FormControl size="small">
            <InputLabel id="selection-type-label">Tipo de selección</InputLabel>
            <Select
              labelId="selection-type-label"
              value={isMultipleChoice ? 'multiple' : 'single'}
              onChange={(e) => {
                setQuestion((prev) => ({ ...prev, responses: {} })); // Limpiar respuestas al cambiar tipo
                setIsMultipleChoice(e.target.value === 'multiple');
              }}
              label="Tipo de selección"
              sx={{ minWidth: 250 }}
            >
              <MenuItem value="single">Selección única (Radio)</MenuItem>
              <MenuItem value="multiple">
                Selección múltiple (Checkbox)
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Sección para configurar la opción "Otra" - ahora visible para ambos tipos */}
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasOtherOption}
                      onChange={handleOtherOptionToggle}
                      color="primary"
                    />
                  }
                  label="¿Desea que esta pregunta tenga una opción 'Otra'?"
                />
              </Box>
              
              {hasOtherOption && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Descripción para la opción 'Otra'"
                    placeholder="Ej: Otra opción (especificar)"
                    value={otherOptionDescription}
                    onChange={(e) => updateOtherOptionDescription(e.target.value)}
                    helperText={
                         'Esta descripción aparecerá como etiqueta de la opción \'Otra\''
                    }
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Input para subir archivo Excel - solo visible para opciones dinámicas */}
        {optionType === 'dynamic' && (
          <Box sx={{ mb: 3 }}>
            <Card>
              <CardHeader title="Cargar opciones desde Excel" />
              <CardContent>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={excelOptions.length > 0}
                  >
                    Seleccionar archivo Excel
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  <Typography
                    variant="body2"
                    color={props.helperText.matrix ? 'error' : 'text.secondary'}
                    sx={{ ml: '2px' }}
                  >
                    {props.helperText.matrix
                      ? props.helperText.matrix
                      : 'Las opciones se tomarán de la primera columna del archivo'}
                  </Typography>
                </Box>
                {excelOptions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="success">
                      Archivo cargado: <strong>{excelFileName}</strong>
                      <br />
                      Se cargaron {excelOptions.length} opciones:{' '}
                      {excelOptions.slice(0, 3).join(', ')}
                      {excelOptions.length > 3 && '...'}
                      {hasOtherOption && (
                        <>
                          <br />
                          <Typography variant="body2" sx={{ mt: 1, color: '#1976d2', fontWeight: 'bold' }}>
                            + Se agregará la opción: "{otherOptionDescription || 'Otra opción'}"
                          </Typography>
                        </>
                      )}
                    </Alert>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setExcelOptions([]);
                        setExcelFileName('');
                        setQuestion((prev) => ({ ...prev, responses: {} }));

                        props.setInformation((prev) => ({
                          ...prev,
                          matrixOption: {
                            ...prev.matrixOption,
                            options: [],
                          },
                        }));
                      }}
                      sx={{ mt: 1 }}
                    >
                      Eliminar archivo cargado
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    {optionType === 'fixed'
                      ? 'Filas (Elementos a evaluar)'
                      : 'Filas (Solo para referencia)'}
                    {optionType === 'fixed' && (
                      <Button onClick={addRow} size="small">
                        <AddIcon fontSize="small" />
                      </Button>
                    )}
                  </Box>
                }
              />
              <CardContent>
                {optionType === 'fixed' ? (
                  <>
                    {question.rows.map((row, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          value={row}
                          placeholder={`Elemento ${index + 1}`}
                          onChange={(e) => updateRow(index, e.target.value)}
                          error={props.errorMessage.matrixRows?.[index] || false}
                          helperText={
                            props.errorMessage.matrixRows?.[index]
                              ? 'Este campo no puede ir vacío'
                              : ''
                          }
                        />
                        <Button
                          onClick={() => removeRow(index)}
                          size="small"
                          variant="outlined"
                          disabled={question.rows.length === 1}
                        >
                          <CloseIcon fontSize="small" />
                        </Button>
                      </Box>
                    ))}
                    
                    {/* Campo "Otra" opción si está habilitado */}
                    {hasOtherOption && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mb: 2,
                          p: 2,
                          backgroundColor: '#f0f7ff',
                          borderRadius: 1,
                          border: '1px solid #2196f3',
                        }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'bold', color: '#1976d2' }}>
                          {otherOptionDescription || 'Otra opción:'}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="El usuario podrá especificar aquí..."
                          disabled
                          sx={{ 
                            backgroundColor: 'white',
                            '& .MuiInputBase-input.Mui-disabled': {
                              color: '#666',
                              WebkitTextFillColor: '#666',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          (Campo para el usuario)
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Para opciones dinámicas, los elementos se evalúan contra las
                    opciones del Excel{hasOtherOption ? ' y la opción adicional configurada' : ''}. Las filas no son necesarias en este
                    modo.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    Columnas (Criterios de evaluación)
                    <Button onClick={addColumn} size="small">
                      <AddIcon fontSize="small" />
                    </Button>
                  </Box>
                }
              />
              <CardContent>
                {question.columns.map((col, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={col}
                      placeholder={`Criterio ${index + 1}`}
                      onChange={(e) => updateColumn(index, e.target.value)}
                      error={props.errorMessage.matrixCols?.[index] || false}
                      helperText={
                        props.errorMessage.matrixCols?.[index]
                          ? 'Este campo no puede ir vacío'
                          : ''
                      }
                    />
                    <Button
                      onClick={() => removeColumn(index)}
                      size="small"
                      variant="outlined"
                      disabled={question.columns.length === 1}
                    >
                      <CloseIcon fontSize="small" />
                    </Button>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={props.isPreviewQuestion}
          onClose={() => props.setIsPreviewQuestion(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Vista previa de la matriz</DialogTitle>
          <DialogContent dividers>
            <div style={{ overflowX: 'auto' }}>
              {optionType === 'fixed' ? (
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Elementos</th>
                      {question.columns.map((column, index) => (
                        <th key={index} style={tableHeaderStyle}>
                          {column || `Columna ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Filas normales */}
                    {question.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td style={tableElementCellStyle}>
                          {row || `Fila ${rowIndex + 1}`}
                        </td>
                        {question.columns.map((_, columnIndex) => (
                          <td key={columnIndex} style={tableCellStyle}>
                            {isMultipleChoice ? (
                              <input
                                type="checkbox"
                                checked={
                                  Array.isArray(question.responses[rowIndex]) &&
                                  question.responses[rowIndex]?.includes(
                                    columnIndex.toString()
                                  )
                                }
                                onChange={(e) => {
                                  setQuestion((prev) => {
                                    const current = Array.isArray(
                                      prev.responses[rowIndex]
                                    )
                                      ? [...prev.responses[rowIndex]]
                                      : [];

                                    const updated = e.target.checked
                                      ? [...current, columnIndex.toString()]
                                      : current.filter(
                                          (val) =>
                                            val !== columnIndex.toString()
                                        );

                                    return {
                                      ...prev,
                                      responses: {
                                        ...prev.responses,
                                        [rowIndex]: updated,
                                      },
                                    };
                                  });
                                }}
                              />
                            ) : (
                              <RadioGroup
                                row
                                value={question.responses[rowIndex] || ''}
                                onChange={() => {}}
                              >
                                <FormControlLabel
                                  value={columnIndex.toString()}
                                  control={<Radio disabled />}
                                  label=""
                                />
                              </RadioGroup>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                    
                    {/* Fila para la opción "Otra" si está habilitada */}
                    {hasOtherOption && (
                      <tr style={{ backgroundColor: '#f0f7ff' }}>
                        <td style={{...tableElementCellStyle, color: '#1976d2'}}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {otherOptionDescription || 'Otra opción:'}
                            </Typography>
                            <TextField
                              size="small"
                              placeholder="Especificar..."
                              sx={{ minWidth: 150, backgroundColor: 'white' }}
                            />
                          </Box>
                        </td>
                        {question.columns.map((_, columnIndex) => (
                          <td key={columnIndex} style={tableCellStyle}>
                            {isMultipleChoice ? (
                              <input
                                type="checkbox"
                                checked={
                                  Array.isArray(question.responses['otherOption']) &&
                                  question.responses['otherOption']?.includes(
                                    columnIndex.toString()
                                  )
                                }
                                onChange={(e) => {
                                  setQuestion((prev) => {
                                    const current = Array.isArray(
                                      prev.responses['otherOption']
                                    )
                                      ? [...prev.responses['otherOption']]
                                      : [];

                                    const updated = e.target.checked
                                      ? [...current, columnIndex.toString()]
                                      : current.filter(
                                          (val) =>
                                            val !== columnIndex.toString()
                                        );

                                    return {
                                      ...prev,
                                      responses: {
                                        ...prev.responses,
                                        otherOption: updated,
                                      },
                                    };
                                  });
                                }}
                              />
                            ) : (
                              <RadioGroup
                                row
                                value={question.responses['otherOption'] || ''}
                                onChange={(e) => {
                                  setQuestion((prev) => ({
                                    ...prev,
                                    responses: {
                                      ...prev.responses,
                                      otherOption: e.target.value,
                                    },
                                  }));
                                }}
                              >
                                <FormControlLabel
                                  value={columnIndex.toString()}
                                  control={<Radio />}
                                  label=""
                                />
                              </RadioGroup>
                            )}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                // Vista previa para opciones dinámicas con select en las filas
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Elementos</th>
                      {question.columns.map((column, index) => (
                        <th key={index} style={tableHeaderStyle}>
                          {column || `Columna ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Fila para el select con opciones dinámicas */}
                    <tr>
                      <td style={tableElementCellStyle}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Seleccionar opción</InputLabel>
                          <Select
                            value={question.responses['dynamicOption'] || ''}
                            onChange={(e) =>
                              handleDynamicResponseChange(
                                'dynamicOption',
                                e.target.value
                              )
                            }
                            displayEmpty
                            label="Seleccionar opción"
                          >
                            <MenuItem value="">
                              <em>Seleccionar opción</em>
                            </MenuItem>
                            {excelOptions.map((option, optionIndex) => (
                              <MenuItem key={optionIndex} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                            {/* Agregar la opción "Otra" al final del select si está habilitada */}
                            {hasOtherOption && (
                              <MenuItem 
                                key="other-option" 
                                value="other"
                                sx={{ 
                                  backgroundColor: '#f0f7ff',
                                  fontWeight: 'bold',
                                  color: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                  },
                                }}
                              >
                                {otherOptionDescription || 'Otra opción'}
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                        
                        {/* Campo de texto para especificar "otra opción" cuando se selecciona */}
                        {question.responses['dynamicOption'] === 'other' && hasOtherOption && (
                          <TextField
                            size="small"
                            placeholder="Especificar otra opción..."
                            sx={{ ml: 2, minWidth: 200, backgroundColor: '#f0f7ff' }}
                            value={question.responses['otherSpecification'] || ''}
                            onChange={(e) => 
                              setQuestion((prev) => ({
                                ...prev,
                                responses: {
                                  ...prev.responses,
                                  otherSpecification: e.target.value,
                                },
                              }))
                            }
                          />
                        )}
                      </td>
                      {question.columns.map((_, columnIndex) => (
                        <td key={columnIndex} style={tableCellStyle}>
                          {isMultipleChoice ? (
                            <input
                              type="checkbox"
                              checked={
                                Array.isArray(
                                  question.responses['dynamicOptionColumns']
                                ) &&
                                question.responses[
                                  'dynamicOptionColumns'
                                ]?.includes(columnIndex.toString())
                              }
                              onChange={(e) => {
                                setQuestion((prev) => {
                                  const current = Array.isArray(
                                    prev.responses['dynamicOptionColumns']
                                  )
                                    ? [
                                        ...prev.responses[
                                          'dynamicOptionColumns'
                                        ],
                                      ]
                                    : [];

                                  const updated = e.target.checked
                                    ? [...current, columnIndex.toString()]
                                    : current.filter(
                                        (val) => val !== columnIndex.toString()
                                      );

                                  return {
                                    ...prev,
                                    responses: {
                                      ...prev.responses,
                                      dynamicOptionColumns: updated,
                                    },
                                  };
                                });
                              }}
                            />
                          ) : (
                            <RadioGroup
                              row
                              value={
                                question.responses['dynamicOptionColumns'] || ''
                              }
                              onChange={(e) => {
                                setQuestion((prev) => ({
                                  ...prev,
                                  responses: {
                                    ...prev.responses,
                                    dynamicOptionColumns: e.target.value,
                                  },
                                }));
                              }}
                            >
                              <FormControlLabel
                                value={columnIndex.toString()}
                                control={<Radio />}
                                label=""
                              />
                            </RadioGroup>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {optionType === 'dynamic' && excelOptions.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Debes cargar un archivo Excel para ver las opciones dinámicas.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => props.setIsPreviewQuestion(false)}
              variant="outlined"
            >
              Cerrar vista previa
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LayoutQuestion>
  );
}